/**
 * Game state and every change a player can make, as one pure reducer.
 * No browser APIs here, so it is easy to test and to reuse.
 *
 * Notes come in two kinds, chosen the first time Notes is switched on in a game:
 *  - manual ("Start blank"): exactly the digits the player writes;
 *  - auto ("Fill all"): every digit each cell can still take, worked out live
 *    from the board, minus the digits the player has struck off.
 */
import { PEERS, colOf, rowOf } from './geometry.ts';
import type { HintStep, PublicPuzzle } from './types.ts';

export type NotesKind = 'none' | 'manual' | 'auto';

/** One cell's before/after, so any move can be undone exactly. */
export interface CellChange {
  cell: number;
  value: [before: number, after: number];
  /** Manual notes (bitmask). */
  notes: [before: number, after: number];
  /** Auto notes the player struck off (bitmask). */
  struck: [before: number, after: number];
}

/** One undoable step: changes to cells, and possibly a switch of notes kind. */
export interface Move {
  cells: CellChange[];
  notesKind?: [before: NotesKind, after: NotesKind];
}

export type HintView =
  /** `notice` explains why this hint isn't counted (seen before, or cycling). */
  | { kind: 'step'; stage: 1 | 2; step: HintStep; notice?: string }
  | { kind: 'message'; text: string };

export type Status = 'playing' | 'paused' | 'won';
/** 'help' = How to play is open; the game resumes by itself when it closes. */
export type PauseReason = 'user' | 'hidden' | 'help';

export interface GameState {
  puzzle: PublicPuzzle;
  given: boolean[];
  values: number[];
  notesKind: NotesKind;
  /** Manual notes per cell as a bitmask: bit d set = note d shown. */
  notes: number[];
  /** Auto notes struck off per cell, as a bitmask. */
  struck: number[];
  undo: Move[];
  redo: Move[];
  selected: number | null;
  notesMode: boolean;
  /** Cells marked wrong by the last Check; a cell drops out when edited. */
  wrong: number[];
  checksUsed: number;
  hintsUsed: number;
  /** Keys of every hint step shown in this game; a key already here is not counted again. */
  seenHints: string[];
  /** Set the first time "Fill all" is used and never cleared, even by Undo. */
  autoNotesUsed: boolean;
  elapsedMs: number;
  status: Status;
  pauseReason: PauseReason | null;
  hint: HintView | null;
  startedAt: string;
}

export type Action =
  | { type: 'select'; cell: number | null }
  | { type: 'move'; dRow: number; dCol: number }
  | { type: 'input'; digit: number }
  | { type: 'erase' }
  | { type: 'toggleNotesMode' }
  /** First use of Notes in a game: fill all possibilities, or start blank. */
  | { type: 'startNotes'; fill: boolean }
  | { type: 'fillNotes' }
  | { type: 'clearNotes' }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'checked'; wrongCells: number[] }
  | { type: 'hint'; hint: HintView }
  | { type: 'revealHint' }
  | { type: 'closeHint' }
  | { type: 'pause'; reason: PauseReason }
  | { type: 'resume' }
  | { type: 'tick'; ms: number }
  | { type: 'won' };

const MAX_HISTORY = 500;
const ALL = 0b1111111110;
const bit = (d: number) => 1 << d;

export function newGame(puzzle: PublicPuzzle, now = new Date()): GameState {
  const values = Array.from(puzzle.givens, Number);
  return {
    puzzle,
    given: values.map((v) => v !== 0),
    values,
    notesKind: 'none',
    notes: new Array(81).fill(0),
    struck: new Array(81).fill(0),
    undo: [],
    redo: [],
    selected: values.findIndex((v) => v === 0),
    notesMode: false,
    wrong: [],
    checksUsed: 0,
    hintsUsed: 0,
    seenHints: [],
    autoNotesUsed: false,
    elapsedMs: 0,
    status: 'playing',
    pauseReason: null,
    hint: null,
    startedAt: now.toISOString(),
  };
}

export const boardString = (s: GameState) => s.values.join('');
export const isFull = (s: GameState) => s.values.every((v) => v !== 0);

/** Digits an empty cell can still take, from its row, column and box only. */
export function possibleAt(values: number[], cell: number): number {
  if (values[cell]) return 0;
  let mask = ALL;
  for (const p of PEERS[cell]) if (values[p]) mask &= ~bit(values[p]);
  return mask;
}

/** What a cell's notes show: `shown` digits, of which `struck` are crossed out. */
export function cellNotes(s: GameState, cell: number): { shown: number; struck: number } {
  if (s.values[cell]) return { shown: 0, struck: 0 };
  if (s.notesKind === 'auto') {
    const shown = possibleAt(s.values, cell);
    return { shown, struck: shown & s.struck[cell] };
  }
  return { shown: s.notes[cell], struck: 0 };
}

/** Empty cells with manual notes, which "Fill all" would replace. */
export const manualNotedCells = (s: GameState) =>
  s.notesKind === 'auto' ? 0 : s.values.reduce((n, v, cell) => n + (v === 0 && s.notes[cell] ? 1 : 0), 0);

/** True when any note is on the board (so "Clear all notes" has something to do). */
export const hasNotes = (s: GameState) =>
  s.notesKind === 'auto' || s.values.some((v, cell) => v === 0 && s.notes[cell] !== 0);

/** Cells whose digit repeats in their row, column or box, and the digits involved. */
export function findClashes(values: number[]): { cells: Set<number>; digits: Set<number> } {
  const cells = new Set<number>();
  const digits = new Set<number>();
  for (let a = 0; a < 81; a++) {
    if (!values[a]) continue;
    for (const b of PEERS[a]) {
      if (values[b] === values[a]) {
        cells.add(a);
        digits.add(values[a]);
      }
    }
  }
  return { cells, digits };
}

/** How many of each digit are on the board (index 1-9). */
export function digitCounts(values: number[]): number[] {
  const counts = new Array(10).fill(0);
  for (const v of values) counts[v]++;
  return counts;
}

function apply(s: GameState, move: Move, direction: 0 | 1): GameState {
  const values = s.values.slice();
  const notes = s.notes.slice();
  const struck = s.struck.slice();
  for (const ch of move.cells) {
    values[ch.cell] = ch.value[direction];
    notes[ch.cell] = ch.notes[direction];
    struck[ch.cell] = ch.struck[direction];
  }
  const touched = new Set(move.cells.filter((c) => c.value[0] !== c.value[1]).map((c) => c.cell));
  return {
    ...s,
    values,
    notes,
    struck,
    notesKind: move.notesKind ? move.notesKind[direction] : s.notesKind,
    wrong: s.wrong.filter((c) => !touched.has(c)),
    hint: null,
  };
}

const same = (c: CellChange) => c.value[0] === c.value[1] && c.notes[0] === c.notes[1] && c.struck[0] === c.struck[1];

/** Records a move (skipping no-ops) and clears redo. */
function commit(s: GameState, move: Move): GameState {
  const cells = move.cells.filter((c) => !same(c));
  const kindChanges = !!move.notesKind && move.notesKind[0] !== move.notesKind[1];
  if (!cells.length && !kindChanges) return s;
  const real: Move = kindChanges ? { cells, notesKind: move.notesKind } : { cells };
  const undo = [...s.undo, real];
  if (undo.length > MAX_HISTORY) undo.shift();
  return { ...apply(s, real, 1), undo, redo: [] };
}

const change = (s: GameState, cell: number, to: Partial<{ value: number; notes: number; struck: number }>): CellChange => ({
  cell,
  value: [s.values[cell], to.value ?? s.values[cell]],
  notes: [s.notes[cell], to.notes ?? s.notes[cell]],
  struck: [s.struck[cell], to.struck ?? s.struck[cell]],
});

function inputDigit(s: GameState, cell: number, digit: number): GameState {
  if (s.given[cell]) return s;
  const value = s.values[cell];

  if (s.notesMode) {
    if (value) return s;
    if (s.notesKind === 'auto') {
      // Strike off or restore a possibility; digits that aren't possible do nothing.
      if (!(possibleAt(s.values, cell) & bit(digit))) return s;
      return commit(s, { cells: [change(s, cell, { struck: s.struck[cell] ^ bit(digit) })] });
    }
    const kind: Move['notesKind'] = s.notesKind === 'none' ? ['none', 'manual'] : undefined;
    return commit(s, { cells: [change(s, cell, { notes: s.notes[cell] ^ bit(digit) })], notesKind: kind });
  }

  if (value === digit) {
    // Tapping the same digit again clears the cell.
    return commit(s, { cells: [change(s, cell, { value: 0 })] });
  }

  // Placing a digit clears the cell's own notes. Manual notes for that digit are
  // cleared in its row, column and box; auto notes update by themselves.
  const cells: CellChange[] = [change(s, cell, { value: digit, notes: 0, struck: 0 })];
  if (s.notesKind !== 'auto') {
    for (const p of PEERS[cell]) {
      if (s.notes[p] & bit(digit)) cells.push(change(s, p, { notes: s.notes[p] & ~bit(digit) }));
    }
  }
  return commit(s, { cells });
}

/** Switches to auto notes with nothing struck off; manual notes are replaced. */
function fill(s: GameState): GameState {
  const cells: CellChange[] = [];
  for (let cell = 0; cell < 81; cell++) cells.push(change(s, cell, { notes: 0, struck: 0 }));
  return { ...commit(s, { cells, notesKind: [s.notesKind, 'auto'] }), autoNotesUsed: true };
}

export function reducer(s: GameState, a: Action): GameState {
  const playing = s.status === 'playing';
  switch (a.type) {
    case 'select':
      return { ...s, selected: a.cell };

    case 'move': {
      const from = s.selected ?? 40;
      const r = (rowOf(from) + a.dRow + 9) % 9;
      const c = (colOf(from) + a.dCol + 9) % 9;
      return { ...s, selected: r * 9 + c };
    }

    case 'input':
      if (!playing || s.selected === null || a.digit < 1 || a.digit > 9) return s;
      return inputDigit(s, s.selected, a.digit);

    case 'erase': {
      // Clears a placed digit, or an empty cell's own notes (auto: its strike-offs).
      if (!playing || s.selected === null || s.given[s.selected]) return s;
      const cell = s.selected;
      return commit(s, { cells: [change(s, cell, { value: 0, notes: 0, struck: 0 })] });
    }

    case 'toggleNotesMode':
      return { ...s, notesMode: !s.notesMode };

    case 'startNotes': {
      if (!playing) return s;
      const on = { ...s, notesMode: true };
      if (a.fill) return fill(on);
      return s.notesKind === 'none' ? { ...on, notesKind: 'manual' } : on;
    }

    case 'fillNotes':
      return playing ? fill(s) : s;

    case 'clearNotes': {
      if (!playing) return s;
      const cells: CellChange[] = [];
      for (let cell = 0; cell < 81; cell++) cells.push(change(s, cell, { notes: 0, struck: 0 }));
      return commit(s, { cells, notesKind: [s.notesKind, s.notesKind === 'none' ? 'none' : 'manual'] });
    }

    case 'undo': {
      if (!playing || !s.undo.length) return s;
      const last = s.undo[s.undo.length - 1];
      return { ...apply(s, last, 0), undo: s.undo.slice(0, -1), redo: [...s.redo, last] };
    }

    case 'redo': {
      if (!playing || !s.redo.length) return s;
      const last = s.redo[s.redo.length - 1];
      return { ...apply(s, last, 1), redo: s.redo.slice(0, -1), undo: [...s.undo, last] };
    }

    case 'checked':
      return { ...s, wrong: a.wrongCells, checksUsed: s.checksUsed + 1, hint: null };

    case 'hint': {
      // Only a step not shown before in this game counts. Messages never count.
      if (a.hint.kind !== 'step') return { ...s, hint: a.hint };
      const key = a.hint.step.key;
      const fresh = !s.seenHints.includes(key);
      return {
        ...s,
        hint: a.hint,
        hintsUsed: s.hintsUsed + (fresh ? 1 : 0),
        seenHints: fresh ? [...s.seenHints, key] : s.seenHints,
      };
    }

    case 'revealHint':
      return s.hint?.kind === 'step' ? { ...s, hint: { ...s.hint, stage: 2 } } : s;

    case 'closeHint':
      return { ...s, hint: null };

    case 'pause':
      if (!playing) return s;
      return { ...s, status: 'paused', pauseReason: a.reason };

    case 'resume':
      if (s.status !== 'paused') return s;
      return { ...s, status: 'playing', pauseReason: null };

    case 'tick':
      return playing ? { ...s, elapsedMs: s.elapsedMs + a.ms } : s;

    case 'won':
      return { ...s, status: 'won', selected: null, hint: null, wrong: [], pauseReason: null };
  }
}

/** "4:07" or "1:02:33". */
export function formatTime(ms: number): string {
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = String(total % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}
